import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SkillTestBuilder } from '../../components/employer/SkillTestBuilder';
import type { SkillTestDraft } from '../../types/skillTests';
import { useSkillTests } from '../../hooks/useSkillTests';
import { toast } from 'sonner';

const initialDraft: SkillTestDraft = {
  title: '',
  description: '',
  category: '',
  duration: 20,
  passingScore: 70,
  questions: [],
  status: 'draft',
};

export function SkillTestCreatePage() {
  const navigate = useNavigate();
  const { createTest } = useSkillTests();

  const handleCancel = () => {
    navigate('/employer/dashboard', { state: { initialTab: 'skill-tests' } });
  };

  const handleSubmit = async (draft: SkillTestDraft) => {
    const saved = await createTest(draft);
    toast.success('Skill test created', {
      description: `${saved.title} is now ready for review.`,
    });
    navigate('/employer/dashboard', { state: { initialTab: 'skill-tests' } });
  };

  return (
    <main className="min-h-screen bg-[#070917] pb-24" aria-labelledby="skill-test-create-title">
      <header className="border-b border-white/10 bg-[#0d1022]/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.35em] text-gray-500">Assessments</p>
              <h1 id="skill-test-create-title" className="text-3xl font-semibold text-white sm:text-4xl">Create a skill test</h1>
              <p className="text-sm text-gray-400">
                Launch a tailored assessment that aligns with your role requirements and candidate expectations.
              </p>
            </div>
            <Badge className="w-fit bg-white/10 text-gray-200">Draft builder</Badge>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={handleCancel}>
              Back to dashboard
            </Button>
            <Button
              asChild
              variant="ghost"
              className="text-gray-300 hover:text-[#f7ef8a] hover:bg-[#ba9036]/10"
            >
              <Link to="/employer/dashboard">View analytics</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <SkillTestBuilder mode="create" initialValue={initialDraft} onSubmit={handleSubmit} onCancel={handleCancel} />
      </main>
    </main>
  );
}
